import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageSquarePlus, Plus } from "lucide-react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useCommentAutomations,
  useUpdateCommentAutomation,
  useDeleteCommentAutomation,
  type CommentAutomation,
} from "@/hooks/use-comment-automations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AutomationEditorSheet } from "@/components/inbox/automations/automation-editor-sheet";
import { AutomationLogsDrawer } from "@/components/inbox/automations/automation-logs-drawer";

function StatCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="min-w-[64px]">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-[13px] font-medium tabular-nums">{value}</div>
    </div>
  );
}

export function InboxAutomationsPage() {
  const { t } = useTranslation("inbox");
  const { brand } = useBrandContext();
  const permissions = usePermissions();
  const canManage = permissions.canManageBrand;

  const automations = useCommentAutomations(brand.id);
  const update = useUpdateCommentAutomation(brand.id);
  const remove = useDeleteCommentAutomation(brand.id);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CommentAutomation | null>(null);
  const [logsFor, setLogsFor] = useState<CommentAutomation | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CommentAutomation | null>(null);

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(a: CommentAutomation) {
    setEditing(a);
    setEditorOpen(true);
  }

  async function toggleActive(a: CommentAutomation, isActive: boolean) {
    try {
      await update.mutateAsync({ automationId: a.id, isActive });
      toast.success(t("automations.toasts.updated"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("automations.toasts.error"));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(t("automations.toasts.deleted"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("automations.toasts.error"));
    } finally {
      setPendingDelete(null);
    }
  }

  const header = (
    <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
      <div>
        <h1 className="text-[15px] font-semibold">{t("automations.title")}</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("automations.subtitle")}
        </p>
      </div>
      {canManage && automations.data && automations.data.length > 0 && (
        <Button size="sm" onClick={openCreate}>
          <Plus size={14} className="mr-1" />
          {t("automations.new")}
        </Button>
      )}
    </div>
  );

  function body() {
    if (automations.isLoading) {
      return (
        <div className="space-y-2 p-5">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }
    if (automations.isError) {
      return (
        <p className="p-8 text-center text-sm text-muted-foreground">
          {t("automations.loadError")}
        </p>
      );
    }
    // null = Zernio not configured for this brand (a setup state, not an error).
    if (automations.data === null) {
      return (
        <EmptyState
          icon={MessageSquarePlus}
          title={t("automations.notConfigured")}
          description={t("automations.notConfiguredHint")}
        />
      );
    }
    const rows = automations.data ?? [];
    if (rows.length === 0) {
      return (
        <EmptyState
          icon={MessageSquarePlus}
          title={t("automations.empty")}
          description={t("automations.emptyHint")}
          action={
            canManage
              ? { label: t("automations.new"), onClick: openCreate }
              : undefined
          }
        />
      );
    }

    return (
      <div className="divide-y divide-border">
        {rows.map((a) => {
          const s = a.stats ?? {};
          const tracked = s.trackedSends ?? 0;
          const ctr =
            tracked > 0 ? `${Math.round(((s.linkClicks ?? 0) / tracked) * 100)}%` : "—";
          return (
            <div key={a.id} className="px-5 py-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium truncate">{a.name}</span>
                    <Badge variant="secondary">
                      {a.trigger === "story_reply"
                        ? t("automations.trigger.storyReply")
                        : t("automations.trigger.comment")}
                    </Badge>
                    {!a.platformPostId && (
                      <Badge variant="outline">
                        {t("automations.accountWideBadge")}
                      </Badge>
                    )}
                    {a.alsoMatchInDms && (
                      <Badge variant="outline">{t("automations.badges.alsoDms")}</Badge>
                    )}
                    {a.audience?.tapToUnlock && (
                      <Badge variant="outline">{t("automations.badges.tapToUnlock")}</Badge>
                    )}
                  </div>
                  {a.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {a.keywords.map((k) => (
                        <Badge key={k} variant="outline" className="text-[11px]">
                          {k}
                        </Badge>
                      ))}
                      {(a.excludeKeywords ?? []).map((k) => (
                        <Badge
                          key={`not-${k}`}
                          variant="outline"
                          className="text-[11px] text-muted-foreground line-through"
                        >
                          {k}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                {canManage && (
                  <Switch
                    checked={a.isActive}
                    aria-label={a.name}
                    onCheckedChange={(v) => toggleActive(a, v)}
                  />
                )}
              </div>

              <div className="flex flex-wrap gap-5">
                <StatCell
                  label={t("automations.stats.delivered")}
                  value={s.delivered ?? 0}
                />
                <StatCell label={t("automations.stats.read")} value={s.read ?? 0} />
                <StatCell
                  label={t("automations.stats.linkClicks")}
                  value={s.linkClicks ?? 0}
                />
                <StatCell
                  label={t("automations.stats.uniqueClicks")}
                  value={s.uniqueClicks ?? 0}
                />
                <StatCell
                  label={t("automations.stats.trackedSends")}
                  value={tracked}
                />
                <StatCell label={t("automations.stats.ctr")} value={ctr} />
              </div>

              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setLogsFor(a)}>
                  {t("automations.actions.logs")}
                </Button>
                {canManage && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)}>
                      {t("automations.actions.edit")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPendingDelete(a)}
                    >
                      {t("automations.actions.delete")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {header}
      {body()}

      <AutomationEditorSheet
        brandId={brand.id}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        automation={editing}
      />

      <AutomationLogsDrawer
        brandId={brand.id}
        automationId={logsFor?.id ?? null}
        automationName={logsFor?.name}
        onOpenChange={(open) => {
          if (!open) setLogsFor(null);
        }}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("automations.confirmDelete.title")}</DialogTitle>
            <DialogDescription>
              {t("automations.confirmDelete.body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t("automations.actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={remove.isPending}
            >
              {t("automations.confirmDelete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
